import type { ImageLightOptions } from './types'

const databaseName = 'image-light-store'
const databaseVersion = 1
const storeName = 'images'
const sourceStoragePrefix = 'image-light-source'
const customColorStoragePrefix = 'image-light-custom-colors'

export type ImageLightSourceKind = 'static' | 'dynamic' | 'color'

export interface PersistedImageLight {
  scope: string
  blob: Blob
  name: string
  type: string
  updatedAt: number
  options: ImageLightOptions
}

const isStorageAvailable = () => typeof indexedDB !== 'undefined'
const sourceKey = (scope: string) => `${sourceStoragePrefix}:${scope}`
const customColorKey = (scope: string) => `${customColorStoragePrefix}:${scope}`

const normalizeCustomColors = (value: unknown): Record<number, number> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null

  return Object.entries(value as Record<string, unknown>).reduce<Record<number, number>>((colors, [key, color]) => {
    const kk = Number(key)
    if (!Number.isFinite(kk) || typeof color !== 'number' || !Number.isFinite(color)) {
      return colors
    }

    colors[kk] = color
    return colors
  }, {})
}

const deleteDatabase = () =>
  new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(databaseName)
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
    request.onblocked = () => resolve()
  })

const openDatabase = (allowRecreate = true): Promise<IDBDatabase> =>
  new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(databaseName, databaseVersion)
    request.onupgradeneeded = () => {
      const database = request.result
      if (!database.objectStoreNames.contains(storeName)) {
        database.createObjectStore(storeName, { keyPath: 'scope' })
      }
    }
    request.onsuccess = async () => {
      const database = request.result
      if (database.objectStoreNames.contains(storeName)) {
        resolve(database)
        return
      }

      database.close()
      if (!allowRecreate) {
        reject(new Error(`IndexedDB store "${storeName}" is missing`))
        return
      }

      try {
        await deleteDatabase()
        resolve(await openDatabase(false))
      } catch (error) {
        reject(error)
      }
    }
    request.onerror = () => reject(request.error)
  })

const withStore = async <T>(mode: IDBTransactionMode, task: (store: IDBObjectStore) => IDBRequest<T>) => {
  const database = await openDatabase()
  return await new Promise<T>((resolve, reject) => {
    const transaction = database.transaction(storeName, mode)
    const request = task(transaction.objectStore(storeName))
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
    transaction.oncomplete = () => database.close()
    transaction.onerror = () => {
      database.close()
      reject(transaction.error)
    }
    transaction.onabort = () => {
      database.close()
      reject(transaction.error)
    }
  })
}

export const imageLightPersistence = {
  async save(scope: string, file: File, options: ImageLightOptions) {
    if (!scope || !isStorageAvailable()) return
    const value: PersistedImageLight = {
      scope,
      blob: file,
      name: file.name,
      type: file.type || 'image/gif',
      updatedAt: Date.now(),
      options,
    }
    await withStore('readwrite', (store) => store.put(value))
  },

  async load(scope: string) {
    if (!scope || !isStorageAvailable()) return null
    const value = await withStore<PersistedImageLight | undefined>('readonly', (store) => store.get(scope))
    return value?.blob ? value : null
  },

  async remove(scope: string) {
    if (!scope || !isStorageAvailable()) return
    await withStore('readwrite', (store) => store.delete(scope))
  },

  async removeByScopePrefix(scopePrefix: string) {
    if (!scopePrefix || !isStorageAvailable()) return
    const database = await openDatabase()
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(storeName, 'readwrite')
      const store = transaction.objectStore(storeName)
      const request = store.openCursor()
      request.onsuccess = () => {
        const cursor = request.result
        if (!cursor) return
        if ((cursor.value as PersistedImageLight).scope.startsWith(scopePrefix)) cursor.delete()
        cursor.continue()
      }
      request.onerror = () => reject(request.error)
      transaction.oncomplete = () => {
        database.close()
        resolve()
      }
      transaction.onerror = () => {
        database.close()
        reject(transaction.error)
      }
      transaction.onabort = () => {
        database.close()
        reject(transaction.error)
      }
    })
  },

  getSource(scope: string): ImageLightSourceKind | null {
    if (!scope || typeof localStorage === 'undefined') return null
    const value = localStorage.getItem(sourceKey(scope))
    return value === 'static' || value === 'dynamic' || value === 'color' ? value : null
  },

  setSource(scope: string, source: ImageLightSourceKind) {
    if (scope && typeof localStorage !== 'undefined') localStorage.setItem(sourceKey(scope), source)
  },

  removeSource(scope: string) {
    if (scope && typeof localStorage !== 'undefined') localStorage.removeItem(sourceKey(scope))
  },

  removeSourcesByScopePrefix(scopePrefix: string) {
    if (!scopePrefix || typeof localStorage === 'undefined') return
    const prefix = `${sourceStoragePrefix}:${scopePrefix}`
    const keys = Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index)).filter((key): key is string => Boolean(key?.startsWith(prefix)))
    keys.forEach((key) => localStorage.removeItem(key))
  },

  getCustomColors(scope: string): Record<number, number> | null {
    if (!scope || typeof localStorage === 'undefined') return null
    const value = localStorage.getItem(customColorKey(scope))
    if (!value) return null

    try {
      return normalizeCustomColors(JSON.parse(value))
    } catch {
      return null
    }
  },

  setCustomColors(scope: string, colors: Record<number, number>) {
    if (!scope || typeof localStorage === 'undefined') return
    localStorage.setItem(customColorKey(scope), JSON.stringify(colors || {}))
  },

  removeCustomColors(scope: string) {
    if (scope && typeof localStorage !== 'undefined') localStorage.removeItem(customColorKey(scope))
  },

  removeCustomColorsByScopePrefix(scopePrefix: string) {
    if (!scopePrefix || typeof localStorage === 'undefined') return
    const prefix = `${customColorStoragePrefix}:${scopePrefix}`
    const keys = Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index)).filter((key): key is string => Boolean(key?.startsWith(prefix)))
    keys.forEach((key) => localStorage.removeItem(key))
  },

  toFile(image: PersistedImageLight) {
    return new File([image.blob], image.name || 'image-light.gif', {
      type: image.type || image.blob.type || 'image/gif',
      lastModified: image.updatedAt || Date.now(),
    })
  },
}
